if (!customElements.get('ql-cart-recommendations')) {
  class QLCartRecommendations extends HTMLElement {
    connectedCallback() {
      if (this.started) return;
      this.started = true;

      this.products = [];
      this.index = 0;
      this.requestVersion = 0;
      this.lastCartKey = null;

      this.root = this.dataset.root || '/';
      if (!this.root.endsWith('/')) this.root += '/';

      this.drawer = this.closest('cart-drawer');
      this.content = this.querySelector('.ql-drawer-recs__content');
      this.controls = this.querySelector('.ql-drawer-recs__controls');

      this.onClick = (event) => {
        const previous = event.target.closest('[data-rec-prev]');
        const next = event.target.closest('[data-rec-next]');

        if (!previous && !next) return;
        if (this.products.length < 2) return;

        const direction = next ? 1 : -1;

        this.index =
          (this.index + direction + this.products.length) %
          this.products.length;

        this.renderProduct();
      };

      this.addEventListener('click', this.onClick);

      this.scheduleRefresh = () => {
        clearTimeout(this.refreshTimer);
        this.refreshTimer = setTimeout(() => this.refresh(), 250);
      };

      /*
       * Watch the drawer's own updates.
       * Ignore mutations made inside this recommendation component.
       */
      if (this.drawer) {
        this.observer = new MutationObserver((mutations) => {
          const cartChanged = mutations.some((mutation) => {
            return !this.contains(mutation.target);
          });

          if (cartChanged) this.scheduleRefresh();
        });

        this.observer.observe(this.drawer, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['class']
        });
      }

      this.refresh();
    }

    disconnectedCallback() {
      this.started = false;
      this.requestVersion += 1;

      clearTimeout(this.refreshTimer);
      this.observer?.disconnect();
      this.controller?.abort();

      this.removeEventListener('click', this.onClick);
    }

    async getJSON(url, signal) {
      const response = await fetch(url, {
        credentials: 'same-origin',
        cache: 'no-store',
        headers: {
          Accept: 'application/json'
        },
        signal
      });

      if (!response.ok) {
        throw new Error(`Request failed: ${response.status}`);
      }

      return response.json();
    }

    async refresh() {
      const version = ++this.requestVersion;

      this.controller?.abort();
      this.controller = new AbortController();

      const signal = this.controller.signal;

      try {
        const cart = await this.getJSON(
          `${this.root}cart.js`,
          signal
        );

        if (!this.isConnected || version !== this.requestVersion) return;

        const cartKey = JSON.stringify({
          currency: cart.currency,
          items: cart.items.map((item) => [
            item.key,
            item.product_id,
            item.quantity
          ])
        });

        if (cartKey === this.lastCartKey) return;

        this.hidden = true;

        if (!cart.items.length) {
          this.products = [];
          this.content.replaceChildren();
          this.lastCartKey = cartKey;
          return;
        }

        this.currency = cart.currency || 'USD';

        const cartProductIds = new Set(
          cart.items.map((item) => String(item.product_id))
        );

        /*
         * Use up to four distinct cart products as recommendation sources.
         * All cart products remain excluded from the results.
         */
        const sourceIds = [...cartProductIds].slice(0, 4);

        const results = await Promise.allSettled(
          sourceIds.map((productId) => {
            const url =
              `${this.root}recommendations/products.json` +
              `?product_id=${encodeURIComponent(productId)}` +
              '&limit=10&intent=related';

            return this.getJSON(url, signal);
          })
        );

        if (!this.isConnected || version !== this.requestVersion) return;

        const successfulResults = results.filter(
          (result) => result.status === 'fulfilled'
        );

        if (!successfulResults.length) {
          throw new Error('Recommendations could not be loaded.');
        }

        const groups = successfulResults.map(
          (result) => result.value.products || []
        );

        const seen = new Set(cartProductIds);
        const selected = [];

        /*
         * Alternate between recommendation groups so multiple cart
         * products can contribute to the final recommendations.
         */
        for (let position = 0; position < 10; position += 1) {
          for (const group of groups) {
            const product = group[position];
            if (!product) continue;

            const id = String(product.id);

            if (seen.has(id)) continue;
            if (!product.available) continue;
            if (Number(product.price) <= 0) continue;

            seen.add(id);
            selected.push(product);

            if (selected.length >= 4) break;
          }

          if (selected.length >= 4) break;
        }

        this.products = selected;
        this.index = 0;

        /*
         * Allow a later retry if one of the recommendation requests failed.
         */
        if (successfulResults.length === results.length) {
          this.lastCartKey = cartKey;
        }

        if (!selected.length) {
          this.content.replaceChildren();
          this.hidden = true;
          return;
        }

        this.controls.hidden = selected.length < 2;
        this.renderProduct();
        this.hidden = false;
      } catch (error) {
        if (error.name === 'AbortError') return;
        if (version !== this.requestVersion) return;

        this.hidden = true;
        this.lastCartKey = null;
      }
    }

    formatMoney(cents) {
      return new Intl.NumberFormat(
        document.documentElement.lang || 'en-US',
        {
          style: 'currency',
          currency: this.currency
        }
      ).format(Number(cents) / 100);
    }

    renderProduct() {
      const product = this.products[this.index];
      if (!product) return;

      /*
       * Keep the recommendation URL supplied by Shopify,
       * including its recommendation tracking parameters.
       */
      const url =
        product.url ||
        `${this.root}products/${encodeURIComponent(product.handle)}`;

      const card = document.createElement('div');
      card.className = 'ql-drawer-recs__card';

      const imageLink = document.createElement('a');
      imageLink.className = 'ql-drawer-recs__image-link';
      imageLink.href = url;
      imageLink.setAttribute('aria-label', product.title);

      const featuredImage = product.featured_image;
      const imageSource =
        typeof featuredImage === 'string'
          ? featuredImage
          : featuredImage?.url || featuredImage?.src;

      if (imageSource) {
        const image = document.createElement('img');

        image.src = imageSource;
        image.alt = product.title;
        image.width = 90;
        image.height = 100;
        image.loading = 'lazy';

        imageLink.append(image);
      }

      const info = document.createElement('div');
      info.className = 'ql-drawer-recs__info';

      const title = document.createElement('a');
      title.className = 'ql-drawer-recs__title';
      title.href = url;
      title.textContent = product.title;

      const price = document.createElement('div');
      price.className = 'ql-drawer-recs__price';
      price.textContent =
        (product.price_varies ? 'From ' : '') +
        this.formatMoney(product.price);

      const button = document.createElement('a');
      button.className = 'ql-drawer-recs__button';
      button.href = url;
      button.textContent = 'VIEW PRODUCT';
      button.setAttribute('aria-label', `View ${product.title}`);

      info.append(title, price, button);
      card.append(imageLink, info);

      this.content.replaceChildren(card);
    }
  }

  customElements.define(
    'ql-cart-recommendations',
    QLCartRecommendations
  );
}